import {
  createContext,
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  useContext,
  useId,
} from 'react';
import { cn } from '../../lib/utils';

/** Field сообщает своему полю, есть ли ошибка и где её текст (aria-invalid, aria-describedby). */
const FieldContext = createContext<{ invalid: boolean; describedBy: string | undefined }>({
  invalid: false,
  describedBy: undefined,
});

function useFieldAria(props: {
  'aria-invalid'?: InputHTMLAttributes<HTMLInputElement>['aria-invalid'] | undefined;
  'aria-describedby'?: string | undefined;
}) {
  const field = useContext(FieldContext);
  return {
    'aria-invalid': props['aria-invalid'] ?? (field.invalid || undefined),
    'aria-describedby': props['aria-describedby'] ?? field.describedBy,
  };
}

const control =
  'h-11 w-full rounded-md border border-control-border bg-canvas px-3 text-base text-ink placeholder:text-muted focus:border-ink focus:shadow-[0_0_0_1px_var(--color-ink)] focus:outline-none disabled:bg-surface-card disabled:text-body [&[readonly]]:bg-surface-card aria-invalid:border-danger aria-invalid:focus:shadow-[0_0_0_1px_var(--color-danger)] md:h-10 md:text-sm';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...props }, ref) {
    const aria = useFieldAria(props);
    return <input ref={ref} className={cn(control, className)} {...props} {...aria} />;
  },
);

/** Поле 6-значного кода из приложения-аутентификатора: крупные моноширинные цифры. */
export function CodeInput({
  className,
  value,
  onValueChange,
  ...props
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> & {
  value: string;
  onValueChange: (value: string) => void;
}) {
  return (
    <Input
      inputMode="numeric"
      autoComplete="one-time-code"
      maxLength={6}
      value={value}
      onChange={(event) => onValueChange(event.target.value.replace(/\D/g, ''))}
      className={cn(
        'h-12 text-center font-mono text-2xl font-medium tracking-[0.3em] tabular-nums md:h-12 md:text-2xl',
        className,
      )}
      {...props}
    />
  );
}

export function Select({ className, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  const aria = useFieldAria(props);
  return (
    <select
      className={cn(control, 'select-chevron appearance-none pr-9', className)}
      {...props}
      {...aria}
    />
  );
}

/** Чекбокс с подписью справа. */
export function Checkbox({
  label,
  className,
  ...props
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & { label: ReactNode }) {
  return (
    <label className={cn('flex cursor-pointer items-start gap-2 text-sm text-ink', className)}>
      <input type="checkbox" className="mt-0.5 size-4 shrink-0 accent-ink" {...props} />
      {label}
    </label>
  );
}

/** Поле формы: подпись, элемент управления и под ним подсказка или ошибка. */
export function Field({
  label,
  error,
  hint,
  className,
  children,
}: {
  label: string;
  error?: string | undefined;
  hint?: string | undefined;
  className?: string | undefined;
  children: ReactNode;
}) {
  const id = useId();
  const note = error ?? hint;
  return (
    <FieldContext.Provider value={{ invalid: Boolean(error), describedBy: note ? id : undefined }}>
      {/* biome-ignore lint/a11y/noLabelWithoutControl: элемент управления передаётся в children */}
      <label className={cn('grid min-w-0 gap-1.5', className)}>
        <span className="text-sm font-medium text-ink">{label}</span>
        {children}
        {note && (
          <span id={id} className={cn('text-xs', error ? 'text-danger' : 'text-muted')}>
            {note}
          </span>
        )}
      </label>
    </FieldContext.Provider>
  );
}

const alertTones = {
  danger: 'bg-danger-soft before:bg-danger',
  success: 'bg-success-soft before:bg-success',
  warning: 'bg-warning-soft before:bg-warning',
  info: 'bg-info-soft before:bg-info',
  neutral: 'border border-hairline bg-canvas before:bg-muted',
} as const;

/** Сообщение над формой или блоком: что произошло и что делать дальше. */
export function Alert({
  tone = 'danger',
  children,
}: {
  tone?: keyof typeof alertTones;
  children: ReactNode;
}) {
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cn(
        'flex items-start gap-3 rounded-md px-4 py-3 text-sm text-ink before:mt-1.5 before:size-2 before:shrink-0 before:rounded-full before:content-[""]',
        alertTones[tone],
      )}
    >
      <div className="min-w-0">{children}</div>
    </div>
  );
}
